import React, { useState, useEffect } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import {
  LightModeOutlined,
  DarkModeOutlined,
  Menu as MenuIcon,
  Search,
  SettingsOutlined,
  ArrowDropDownOutlined,
} from "@mui/icons-material";
import FlexBetween from "../components/FlexBetween";
import { useDispatch } from "react-redux";
import { setMode } from "../state";
import profileImage from "../assets/profile.jpeg";
import {
  AppBar,
  Button,
  Box,
  Typography,
  IconButton,
  InputBase,
  Toolbar,
  Menu,
  MenuItem,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from "@mui/material";
import { userLogoutActionFn } from "../redux/actions/userAuthAction.js";
import { getStoredUser } from "../utils/storedUser";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as yup from "yup";
import io from "socket.io-client";

const Navbar = ({ user, isSidebarOpen, setIsSidebarOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const userObj = getStoredUser() || {};

  const isOpen = Boolean(anchorEl);
  const [isModalOpen, setModalOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [_id, set_id] = useState("");
  const [isActive, setIsActive] = useState(false);
  const SOCKET_SERVER_URL = axios.defaults.baseURL || "http://localhost:8000";

  // useEffect(() => {
  //   const socket = io(SOCKET_SERVER_URL, { query: { token: token } });

  //   socket.on("connect", () => {
  //     console.log("Connected to WebSocket server");

  //     // Emit an event to request the user's active status
  //     socket.emit("checkStatus");

  //     // Listen for the server's response
  //     socket.on("status", status => {
  //       setIsActive(status);
  //       console.log("status", status);
  //     });
  //   });

  //   socket.on("disconnect", () => {
  //     console.log("Disconnected from WebSocket server");
  //   });

  //   return () => {
  //     socket.disconnect();
  //   };
  // }, []);

  useEffect(() => {
    const socket = io(SOCKET_SERVER_URL, {
      query: { token: localStorage.getItem("accessToken") },
    });

    // The access token expires every 15 minutes and gets silently
    // refreshed elsewhere (axios/RTK interceptors), but this socket
    // captured whatever token existed at connect time. If it ever
    // disconnects and reconnects (network blip, server restart, ping
    // timeout) after the token has expired, auth fails forever with the
    // same stale token — which is exactly what made presence get stuck
    // on "inactive". Pull the current token fresh before every
    // reconnection attempt instead.
    socket.io.on("reconnect_attempt", () => {
      socket.io.opts.query = { token: localStorage.getItem("accessToken") };
    });

    socket.on("connect", () => {
      console.log("Connected to WebSocket server");

      // Function to emit "checkStatus" until a response is received
      const emitCheckStatus = () => {
        if (!statusReceived) {
          socket.emit("checkStatus");
          console.log("checkStatus requested");

          setTimeout(emitCheckStatus, 1000);
        }
      };

      let statusReceived = false;

      // Start emitting "checkStatus"
      emitCheckStatus();

      // Listen for the server's response
      socket.on("status", (status) => {
        setIsActive(status);
        statusReceived = true;
        console.log("status", status);
      });
    });

    socket.on("disconnect", (reason) => {
      console.log("Disconnected from WebSocket server:", reason);
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    let logout = dispatch(userLogoutActionFn());
    if (logout) {
      navigate("/auth/login");
    }
  };

  const compareAdminOldPassword = async (value) => {
    try {
      const response = await axios.post(
        `/admin/checkOldPassword?adminId=${_id}`,
        {
          oldPassword: value,
        }
      );
      return response.data.success;
    } catch (error) {
      return false;
    }
  };

  const validationSchema = yup.object().shape({
    oldPassword: yup
      .string()
      .required("Old Password is required")
      .test("is-unique", "Old password doesn't match", async function (value) {
        try {
          const success = await compareAdminOldPassword(value);
          console.log("success:", success);
          return success;
        } catch (error) {
          console.error("Error:", error);
          return false;
        }
      }),
    newPassword: yup
      .string()
      .notOneOf(
        [yup.ref("oldPassword")],
        "New password must be different from old password"
      )
      .required("New Password is required"),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("newPassword"), null], "Passwords must match")
      .required("Please confirm your password"),
  });

  const changePassword = async (_id) => {
    setModalOpen(true);
    set_id(_id);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleBlur = async (e) => {
    const fieldName = e.target.name;
    try {
      await validationSchema.validateAt(fieldName, formData);
      setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
    } catch (error) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [fieldName]: error.message,
      }));
    }
  };

  const handlePasswordChange = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      const response = await axios.put(`/admin/changePassword?adminId=${_id}`, {
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword,
      });
      toast.success("Password changed successfully");
      setModalOpen(false);
    } catch (error) {
      if (error.name === "ValidationError") {
        const validationErrors = {};
        error.inner.forEach((validationError) => {
          validationErrors[validationError.path] = validationError.message;
        });
        setErrors(validationErrors);
      } else {
        console.error("Error changing password:", error);
        toast.error("Failed to change password");
      }
    }
  };

  return (
    <AppBar
      sx={{
        position: "static",
        background: "none",
        boxShadow: "none",
      }}
    >
      <ToastContainer />
      <Toolbar sx={{ justifyContent: "space-between" }}>
        {/* LEFT SIDE */}
        <FlexBetween>
          <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
            <MenuIcon />
          </IconButton>
        </FlexBetween>

        {/* RIGHT SIDE */}
        <FlexBetween gap="1.5rem">
          <IconButton onClick={() => dispatch(setMode())}>
            {theme.palette.mode === "dark" ? (
              <DarkModeOutlined sx={{ fontSize: "25px" }} />
            ) : (
              <LightModeOutlined sx={{ fontSize: "25px" }} />
            )}
          </IconButton>
          <FlexBetween>
            <Button
              onClick={handleClick}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                textTransform: "none",
                gap: "1rem",
              }}
            >
              <Box
                component="img"
                alt="profile"
                src={profileImage}
                height="32px"
                width="32px"
                borderRadius="50%"
                sx={{ objectFit: "cover" }}
              />
              <Box textAlign="left">
                <Typography
                  fontWeight="bold"
                  fontSize="0.85rem"
                  sx={{ color: theme.palette.secondary[100] }}
                >
                  {user.name}
                </Typography>
                <Typography
                  fontSize="0.75rem"
                  sx={{ color: theme.palette.secondary[200] }}
                >
                  {user.occupation}
                </Typography>
                <Typography
                  fontSize="0.75rem"
                  sx={{ color: isActive ? "green" : "red" }}
                >
                  {isActive ? "Active" : "Inactive"}
                </Typography>
              </Box>
              <ArrowDropDownOutlined
                sx={{ color: theme.palette.secondary[300], fontSize: "25px" }}
              />
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={isOpen}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <MenuItem onClick={() => changePassword(userObj._id)}>
                Change Password
              </MenuItem>
              <MenuItem onClick={handleLogout}>Log Out</MenuItem>
            </Menu>
          </FlexBetween>
        </FlexBetween>
        <Dialog open={isModalOpen} onClose={() => setModalOpen(false)}>
          <form>
            <DialogTitle>Change Password</DialogTitle>
            <DialogContent>
              <TextField
                label="Old Password"
                type="password"
                name="oldPassword"
                value={formData.oldPassword}
                onBlur={handleBlur}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
              {errors.oldPassword && (
                <Grid item>
                  <Typography color="error">{errors.oldPassword}</Typography>
                </Grid>
              )}
              <TextField
                label="New Password"
                type="password"
                name="newPassword"
                value={formData.newPassword}
                onBlur={handleBlur}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
              {errors.newPassword && (
                <Grid item>
                  <Typography color="error">{errors.newPassword}</Typography>
                </Grid>
              )}
              <TextField
                label="Confirm Password"
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onBlur={handleBlur}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
              {errors.confirmPassword && (
                <Grid item>
                  <Typography color="error">
                    {errors.confirmPassword}
                  </Typography>
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setModalOpen(false)} color="primary">
                Cancel
              </Button>
              <Button
                onClick={handlePasswordChange}
                color="primary"
                variant="contained"
              >
                Change Password
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
